import React from 'react';
import { usePWA } from '@/lib/pwa';
import { Button } from '@/components/ui/button';
import { Download, Smartphone, X, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface PWAInstallButtonProps {
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'sm' | 'default' | 'lg';
  className?: string;
}

export function PWAInstallButton({ 
  variant = 'outline', 
  size = 'default',
  className = ''
}: PWAInstallButtonProps) {
  const { canInstall, isInstalled, installApp } = usePWA();
  const { t } = useTranslation(['common']);

  if (isInstalled) {
    return (
      <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
        <Check className="h-4 w-4" />
        <span className="text-sm font-medium">Aplikácia je nainštalovaná</span>
      </div>
    );
  }

  if (!canInstall) {
    return (
      <div className="text-sm text-muted-foreground">
        <p>Pre inštaláciu použite inštrukcie nižšie.</p>
      </div>
    );
  }

  return (
    <Button
      variant={variant}
      size={size}
      onClick={installApp}
      className={`gap-2 ${className}`}
    >
      <Download className="h-4 w-4" />
      {t('app.install') || 'Inštalovať aplikáciu'}
    </Button>
  );
}

export function PWAUpdateNotification() {
  const { updateAvailable, updateApp } = usePWA();
  const { t } = useTranslation(['common']);

  if (!updateAvailable) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 bg-primary text-primary-foreground p-4 rounded-lg shadow-lg z-50 max-w-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Smartphone className="h-5 w-5" />
          <div>
            <p className="font-medium">{t('app.updateAvailable')}</p>
            <p className="text-sm opacity-90">{t('app.updateDescription')}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={updateApp}
            className="text-xs"
          >
            {t('app.update')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => window.location.reload()}
            className="text-xs p-1 h-6 w-6"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export function PWAMobileInstructions() {
  const { isInstalled } = usePWA();
  const { t } = useTranslation(['common']);

  if (isInstalled) {
    return null;
  }

  // Show instructions for mobile browsers
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  const isMobile = isIOS || isAndroid;

  if (!isMobile) {
    return null;
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
      <div className="flex items-start gap-3">
        <Smartphone className="h-5 w-5 text-blue-600 mt-0.5" />
        <div>
          <h3 className="font-medium text-blue-900 mb-1">
            {t('app.mobileInstall.title')}
          </h3>
          <p className="text-sm text-blue-700 mb-2">
            {t('app.mobileInstall.description')}
          </p>
          <div className="text-sm text-blue-600">
            {isIOS ? (
              <div>
                <p>• {t('app.mobileInstall.ios.step1')}</p>
                <p>• {t('app.mobileInstall.ios.step2')}</p>
                <p>• {t('app.mobileInstall.ios.step3')}</p>
              </div>
            ) : (
              <div>
                <p>• {t('app.mobileInstall.android.step1')}</p>
                <p>• {t('app.mobileInstall.android.step2')}</p>
                <p>• {t('app.mobileInstall.android.step3')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}